import { useEffect } from 'react';
import { Modal, Form } from 'antd';
import { applyCpfCnpjMask, removeMask } from '@/utils/mask';
import { useDefaultFormErrorNotification } from '@/hooks';

import { AxiosError } from 'axios';

import {
  CreateInsurerRequest,
  Insurer,
  UpdateInsurerRequest,
} from '@/core/domain/people/Insurers/Insurers.types';
import {
  useCreateInsurer,
  useUpdateInsurer,
} from '@/core/domain/people/Insurers/Insurers.hooks';
import InsurerSimplifiedForm from '../InsurerSimplifiedForm';

export interface InsurerSimplifiedFormData {
  name: string;
  cnpj?: string;
  fantasyName?: string;
  stateRegistration?: string;
  email?: string;
}

interface InsurerSimplifiedRegisterModalProps {
  visible: boolean;
  handleVisible: (insurer?: Insurer) => void;
  insurer?: Insurer;
  onFinishCallback?: (insurer: Insurer) => void;
}

function InsurerSimplifiedRegisterModal({
  visible,
  handleVisible,
  insurer,
  onFinishCallback,
}: InsurerSimplifiedRegisterModalProps) {
  const [form] = Form.useForm<InsurerSimplifiedFormData>();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  const createMutation = useCreateInsurer();
  const updateMutation = useUpdateInsurer();

  useEffect(() => {
    if (insurer && visible) {
      let formObj: InsurerSimplifiedFormData = {
        name: insurer.name,
        fantasyName: insurer.fantasyName,
        stateRegistration: insurer.stateRegistration,
        email: insurer.email,
      };

      if (insurer.cnpj) {
        formObj = {
          ...formObj,
          cnpj: applyCpfCnpjMask(insurer.cnpj),
        };
      }

      form.setFieldsValue(formObj);
    }
  }, [insurer, visible]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();

      const obj: CreateInsurerRequest = {
        ...values,
        cnpj: values.cnpj || undefined,
        active: true,
      };

      const result = await createMutation.mutateAsync(obj);

      if (onFinishCallback) {
        onFinishCallback(result);
      }

      handleCancel();
    } catch (err) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        sendFormErrorNotification(err);
      }
    }
  };

  const handleEdit = async () => {
    try {
      if (!insurer) return;

      const values = await form.validateFields();

      const obj: UpdateInsurerRequest = {
        ...values,
        cnpj: values.cnpj ? removeMask(values.cnpj) : undefined,
        active: true,
        insurerId: insurer.id,
      };

      const result = await updateMutation.mutateAsync(obj);

      if (onFinishCallback) {
        onFinishCallback(result);
      }

      handleCancel();
    } catch (err) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        sendFormErrorNotification(err);
      }
    }
  };

  const handleOk = async () => {
    if (!insurer) {
      await handleCreate();
    } else {
      await handleEdit();
    }
  };

  const handleCancel = () => {
    handleVisible();
    form.resetFields();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      title="Cadastro simplifificado de seguradora"
      width={991}
      confirmLoading={createMutation.isLoading || updateMutation.isLoading}
    >
      <InsurerSimplifiedForm form={form} />
    </Modal>
  );
}

export default InsurerSimplifiedRegisterModal;
